'use client'

import {
    CatalogCategory,
    CatalogType,
    createCatalogType,
    getCatalogTypesByOrganization,
    updateCatalogType,
} from '@/lib/api/catalog/catalog-api'
import { useResourcePermissions } from '@/lib/hooks/use-resource-permissions'
import { useCatalogAccess } from '@/lib/hooks/use-catalog-access'
import { useCatalogCategoryLabels } from '@/lib/hooks/use-service-label'
import { useAuth } from '@/store/hooks'
import { useEffect, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { MdArrowBack, MdCategory, MdSave } from 'react-icons/md'
import { toast } from 'react-toastify'

const CATEGORY_VALUES: CatalogCategory[] = [
    'CONSULTATION',
    'EXAM',
    'PROCEDURE',
    'TELECONSULTATION',
]

export default function CatalogTypeFormPage() {
    const { user } = useAuth()
    // O rótulo da categoria varia por segmento (Procedimento na saúde, Pedido no resto).
    const categoryLabels = useCatalogCategoryLabels()
    const { allowedCategories } = useCatalogAccess()
    const { canCreate, canUpdate } = useResourcePermissions('registrations', 'catalog')
    const navigate = useNavigate()
    const { id } = useParams()
    const [searchParams] = useSearchParams()
    const isEdit = !!id

    const initialCategory = (searchParams.get('category') as CatalogCategory) || 'EXAM'

    const [loading, setLoading] = useState(false)
    const [loadingData, setLoadingData] = useState(isEdit)
    const [name, setName] = useState('')
    const [category, setCategory] = useState<CatalogCategory>(initialCategory)
    // Só as categorias que o usuário vê no menu; na edição, a do tipo fica mesmo
    // que não esteja mais liberada, para não sumir da tela.
    const categories = CATEGORY_VALUES.filter(
        value =>
            allowedCategories.includes(value) || (isEdit && value === category)
    ).map(value => ({ value, label: categoryLabels[value] }))

    // Padrão (Exame) ou categoria da URL que não está liberada cai na primeira que está.
    useEffect(() => {
        if (isEdit || allowedCategories.includes(category)) return
        const firstAllowed = CATEGORY_VALUES.find(c =>
            allowedCategories.includes(c)
        )
        if (firstAllowed) setCategory(firstAllowed)
    }, [isEdit, category, allowedCategories])

    useEffect(() => {
        if (isEdit && user?.organizationId) {
            setLoadingData(true)
            getCatalogTypesByOrganization(user.organizationId)
                .then(res => {
                    if (res.success) {
                        const item = res.result.find(t => t.id === Number(id))
                        if (item) {
                            setName(item.name)
                            setCategory(item.category)
                        } else {
                            toast.error('Tipo não encontrado')
                            navigate('/registrations/catalog/types')
                        }
                    }
                })
                .finally(() => setLoadingData(false))
        }
    }, [id, isEdit, user?.organizationId, navigate])

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()

        if (!name.trim()) {
            toast.error('Informe o nome do tipo')
            return
        }

        if (!user?.organizationId) return

        setLoading(true)
        try {
            if (isEdit) {
                const response = await updateCatalogType(Number(id), { name: name.trim() })
                if (response.success) {
                    toast.success('Tipo atualizado com sucesso')
                    navigate('/registrations/catalog/types')
                } else {
                    toast.error(response.error || 'Erro ao atualizar tipo')
                }
            } else {
                const response = await createCatalogType({
                    name: name.trim(),
                    category,
                    organizationId: user.organizationId,
                })
                if (response.success) {
                    toast.success('Tipo criado com sucesso')
                    navigate('/registrations/catalog/types')
                } else {
                    toast.error(response.error || 'Erro ao criar tipo')
                }
            }
        } catch (error) {
            toast.error(isEdit ? 'Erro ao atualizar tipo' : 'Erro ao criar tipo')
        } finally {
            setLoading(false)
        }
    }

    if (loadingData) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
                <div className="loading loading-spinner loading-lg text-secondary"></div>
                <p className="text-base-content/60 text-sm">Carregando...</p>
            </div>
        )
    }

    const canSave = isEdit ? canUpdate : canCreate

    return (
        <div className="flex flex-col gap-6">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <button
                        onClick={() => navigate('/registrations/catalog/types')}
                        className="btn btn-ghost btn-sm"
                    >
                        <MdArrowBack className="text-xl" />
                    </button>
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-secondary to-secondary/70 flex items-center justify-center shadow-lg">
                        <MdCategory className="text-2xl text-white" />
                    </div>
                    <div>
                        <h1 className="sm:text-lg lg:text-2xl font-bold text-base-content">
                            {isEdit ? 'Editar Tipo' : 'Novo Tipo de Catálogo'}
                        </h1>
                        <p className="text-sm text-base-content/60">
                            {isEdit
                                ? 'Altere as informações do tipo'
                                : 'Preencha os dados para criar um novo tipo'}
                        </p>
                    </div>
                </div>
            </div>

            <form onSubmit={handleSubmit}>
                <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-6">
                    <div className="flex flex-col gap-5">
                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-medium">
                                    Nome <span className="text-error">*</span>
                                </span>
                            </label>
                            <input
                                type="text"
                                placeholder="Ex: Avaliação, Manutenção, Retorno..."
                                className="input input-bordered w-full"
                                value={name}
                                onChange={e => setName(e.target.value)}
                                required
                            />
                        </div>

                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-medium">
                                    Categoria <span className="text-error">*</span>
                                </span>
                            </label>
                            <select
                                className="select select-bordered w-full"
                                value={category}
                                onChange={e => setCategory(e.target.value as CatalogCategory)}
                                disabled={isEdit}
                            >
                                {categories.map(cat => (
                                    <option key={cat.value} value={cat.value}>
                                        {cat.label}
                                    </option>
                                ))}
                            </select>
                            {isEdit && (
                                <label className="label">
                                    <span className="label-text-alt text-base-content/50">
                                        A categoria não pode ser alterada após a criação
                                    </span>
                                </label>
                            )}
                        </div>
                    </div>

                    <div className="flex justify-end gap-3 mt-8 pt-4 border-t border-base-300/50">
                        <button
                            type="button"
                            className="btn btn-ghost"
                            onClick={() => navigate('/registrations/catalog/types')}
                        >
                            Cancelar
                        </button>
                        {canSave && (
                            <button
                                type="submit"
                                className="btn btn-secondary gap-2"
                                disabled={loading}
                            >
                                {loading ? (
                                    <span className="loading loading-spinner loading-sm"></span>
                                ) : (
                                    <MdSave className="text-xl" />
                                )}
                                {isEdit ? 'Salvar' : 'Criar'}
                            </button>
                        )}
                    </div>
                </div>
            </form>
        </div>
    )
}
